import { call, put, select } from 'redux-saga/effects'

import getQueryParams from '../../../../utils/getQueryParams'
import { getCurrentDate } from '../../../../helpers/getCurrentDate'
import {
  setCurrentCategories, setHasTitles, setHasCategories, setCurrentCollectionIndex,
} from '../../../actions/ux'
import { setProducts } from '../../../actions/products'
import { setFeedMode, setFeedPage } from '../../../actions/feed'
import manipulateCategories from '../../../manipulators/2.0/categories'
import manipulateProducts from '../../../manipulators/2.0/products'
import { fetchFlyerGibs } from '../../../../api/products/fetchFlyerGibs'
import { fetchFlyerGibsCount } from '../../../../api/products/fetchFlyerGibsCount'
import { fetchFlyerGibsPage } from '../../../../api/products/fetchFlyerGibsPage'
import { fetchFlyerGib } from '../../../../api/products/fetchFlyerGib'
import { setCategories } from '../../../actions/categories'
import { getCampaignDataFromUrl } from '../../../../helpers/getInfoFromUrl'
import { ERROR_TYPE_ERROR, FEED_MODE_THRESHOLD, FEED_PAGE_SIZE } from '../../../../helpers/const'
import { renderSplash } from '../../../../splash/index'
import groupProductsByCategory from '../../../manipulators/2.0/groupProductsByCategory'
import { setCategorizedProducts } from '../../../actions/categorizedProducts'
import { selectCollectionProductsList } from '../../../selectors/products'
import { selectLabel } from '../../../selectors/i18n'
import { selectCurrentTheme } from '../../../selectors/ux'

/*
  Feed mode init: fetch the first page only, no category grouping/filters.
  Pagination and search are handled on demand (see sagas/api/resources/products).
*/
function* feedProductsJob({
  flyerId, productId, country, currentDate, isPreview, favouriteProductIds, totalCount,
}) {
  const { data } = (yield call(fetchFlyerGibsPage, {
    country, flyerId, page: 1, limit: FEED_PAGE_SIZE,
  })) || {}
  const productsArray = data || []

  const { products } = manipulateProducts(
    productsArray,
    currentDate,
    isPreview,
    favouriteProductIds,
  )
  const items = Object.keys(products)

  /*
    Deep link to a product living in a page not yet loaded: fetch the single
    gib and merge it before setProducts, so validateProduct passes unchanged.
    A failed fetch leaves the product missing -> validateProduct renders NOT_FOUND.
  */
  if (productId && !products[productId]) {
    try {
      const gib = yield call(fetchFlyerGib, { country, flyerId, gibId: productId })
      if (gib) {
        const { products: gibProducts } = manipulateProducts(
          [gib],
          currentDate,
          isPreview,
          favouriteProductIds,
          totalCount,
        )
        Object.assign(products, gibProducts)
      }
    } catch (error) { console.error(error) } // eslint-disable-line no-console
  }

  yield put(setProducts(products))
  yield put(setFeedPage({ items, page: 1, totalCount }))
  // search must stay visible regardless of the first page contents
  yield put(setHasTitles(true))
  yield put(setHasCategories(false))

  if (productId) {
    const currentCollectionIndex = items.indexOf(productId)
    yield put(setCurrentCollectionIndex(
      currentCollectionIndex >= 0 ? currentCollectionIndex : undefined,
    ))
  }

  return { products, productsArray }
}

// eslint-disable-next-line consistent-return
export function* productsJob({ flyerId, campaignCategories, productId }) {
  try {
    const {
      preview, category, favourites: favouritesProducts, feedmode,
    } = getQueryParams()
    const { country } = getCampaignDataFromUrl()
    const currentDate = getCurrentDate()
    const isPreview = preview === 'true'
    const favouriteProductIds = favouritesProducts ? favouritesProducts.split(',') : []

    const countResponse = yield call(fetchFlyerGibsCount, { country, flyerId })
    const totalCount = countResponse?.metadata?.count || 0
    const isFeedMode = feedmode === 'true' || totalCount > FEED_MODE_THRESHOLD

    yield put(setFeedMode(isFeedMode))

    if (isFeedMode) {
      return yield call(feedProductsJob, {
        flyerId, productId, country, currentDate, isPreview, favouriteProductIds, totalCount,
      })
    }

    const productsArray = (yield call(fetchFlyerGibs, { country, flyerId })) || {}

    const {
      products,
      productCategories,
      hasTitles,
    } = manipulateProducts(productsArray, currentDate, isPreview, favouriteProductIds)

    yield put(setProducts(products))

    const otherDealsLabel = yield select(selectLabel('otherDeals'))
    const categorizedProducts = groupProductsByCategory(
      Object.values(products),
      otherDealsLabel,
    )

    yield put(setCategorizedProducts(categorizedProducts))
    yield put(setHasTitles(hasTitles))
    yield put(setHasCategories(!!(productCategories.length)))

    /*
      Needed to set the initial value for currentCollectionIndex when we land
      on ProductsImageSwiper. Otherwise we cannot keep track of Swiper's slides
      initial index based on categorized products list.
    */
    if (productId) {
      const collectionProductsList = yield select(selectCollectionProductsList)
      const currentCollectionIndex = collectionProductsList
        .filter(product => product.id === productId)?.[0]?.collectionIndex
      yield put(setCurrentCollectionIndex(currentCollectionIndex))
    }

    const categories = manipulateCategories(campaignCategories, productCategories)
    yield put(setCategories(productCategories))

    if (category) {
      const queryStringCategory = category.replace(/"/g, '')
      const filteredCategory = categories.find(el => (
        el.toUpperCase() === queryStringCategory.toUpperCase()
      ))

      if (filteredCategory) {
        yield put(setCurrentCategories([filteredCategory]))
      }
    }
    return { products, productsArray }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)
    const theme = yield select(selectCurrentTheme)
    return renderSplash({
      type: ERROR_TYPE_ERROR,
      message: 'Products Error',
      theme,
    })
  }
}
